import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { BASE_URL } from "./commonComponents/ApiEndpoints";
import toast from "react-hot-toast";
import { useAuth } from "./AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useSidebar } from "./SidebarContext";
import ProfileDropdown from "./commonComponents/ProfileDropdown";
import { Container, Card, Row, Col, Button } from "react-bootstrap";
import { BsPersonBadge, BsEnvelope, BsCheckCircle, BsListCheck, BsKey, BsArrowLeft, BsCheckLg } from "react-icons/bs";
import { LuGraduationCap } from "react-icons/lu";
import { useInstitute } from "./commonComponents/InstituteContext";

const AssignUserRole = () => {
  const { token, logout } = useAuth();
  const { isCollapsed } = useSidebar();
  const { id } = useParams();
  const {logo} = useInstitute();
  const location = useLocation();
  const { userData } = location.state || {};
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "success" or "error"
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [userType, setUserType] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [marginLeft, setMarginLeft] = useState('0px');
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245;
        const collapsedWidth = 85;
        const gap = 1;

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        );
      } else {
        setMarginLeft('0px');
      }

      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);

  const allAvailableRoles = [
    { value: "admin", label: "Administrator", icon: "👑", color: "danger" },
    { value: "accountant", label: "Accountant", icon: "👨‍🏫", color: "primary" },
    { value: "fees_collector", label: "Fee Collector", icon: "💰", color: "success" },
    { value: "marketer", label: "Marketer", icon: "📈", color: "warning" },
    { value: "bus_attendant", label: "Bus Attendant", icon: "🚌", color: "info" },
    { value: "manage_student", label: "Manage Students", icon: "🎓", color: "dark" },
    { value: "manage_courses", label: "Manage Courses", icon: "📚", color: "secondary" },
    { value: "manage_classes", label: "Manage Classes", icon: "🏫", color: "info" },
    { value: "manage_guardians", label: "Manage Guardians", icon: "👪", color: "warning" },
    { value: "manage_announcements", label: "Manage Announcements", icon: "📣", color: "dark" },
    { value: "manage_exams", label: "Manage Exams", icon: "📝", color: "danger" },
  ];

  const roleAliases = {
    administrator: "admin",
    fee_collector: "fees_collector",
    manage_students: "manage_student",
    parent: "parents",
  };

  const normalizeRoleValue = (roleValue) => {
    if (!roleValue) return null;
    const normalized = String(roleValue).toLowerCase().trim().replace(/\s+/g, "_");
    return roleAliases[normalized] || normalized;
  };

  // 🔹 Fetch current user roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          BASE_URL + `/get/user/role/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.data.status) {
          const normalizedRoles = (res.data.roles || [])
            .map((r) => normalizeRoleValue(r.role))
            .filter(Boolean);
          setSelectedRoles(Array.from(new Set(normalizedRoles)));
          setUserType(normalizeRoleValue(res.data.user_type));
        }
      } catch (err) {
        showMessage("Failed to load user roles.", "error");
      } finally {
        setLoading(false);
      }
    };
    if (id && token) {
      fetchRoles();
    }
  }, [id, token]);

  // Checkbox toggle
  const handleCheckboxChange = (role) => {
    const normalizedRole = normalizeRoleValue(role);
    if (!normalizedRole) return;
    // Prevent toggling if role matches user_type
    if (normalizedRole === userType) {
      return;
    }
    setSelectedRoles(prev =>
      prev.includes(normalizedRole)
        ? prev.filter(r => r !== normalizedRole)
        : [...prev, normalizedRole]
    );
  };

  // Show message helper
  const showMessage = (msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 5000);
  };

  // Submit roles
  const handleSubmit = async () => {
    if (!userData?.email) {
      showMessage("User email is missing.", "error");
      return;
    }

    const instituteId = Cookies.get("institute_id");
    const payload = {
      user_id: Number(id),
      email: userData.email,
      role: selectedRoles.map((role) => normalizeRoleValue(role)).filter(Boolean),
      institute_id: Number(instituteId),
    };

    try {
      setLoading(true);
      const res = await axios.post(`${BASE_URL}/user/set/role/`, payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status) {
        showMessage("✅ Roles updated successfully!", "success");
      } else {
        showMessage(res.data.message || "Failed to update roles.", "error");
      }
    } catch (error) {
      const resData = error.response?.data;
      if (resData?.message) showMessage(resData.message, "error");
      if (resData?.errors) {
        Object.values(resData.errors).forEach(errArr =>
          errArr.forEach(msg => showMessage(msg, "error"))
        );
      }
      if (!resData) showMessage("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Badge color
  const getRoleColor = (roleValue) => {
    const role = allAvailableRoles.find(r => r.value === roleValue);
    return role ? role.color : "secondary";
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Handle password change
  const handleChangePassword = async () => {
    if (!newPassword.trim()) {
      toast.error("Please enter a new password");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    try {
      setChangingPassword(true);
      const payload = {
        user_id: Number(id),
        new_password: newPassword,
      };

      const res = await axios.post(
        `${BASE_URL}/users/change-password/by-id`,
        payload,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.status || res.data.message) {
        toast.success(res.data.message || "Password changed successfully!");
        setNewPassword("");
        showMessage("✅ Password changed successfully!", "success");
      } else {
        toast.error(res.data.message || "Failed to change password");
        showMessage("Failed to change password.", "error");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to change password. Please try again.";
      toast.error(errorMessage);
      showMessage(errorMessage, "error");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          marginRight: '0px',
          marginBottom: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              {/* Left: Logo, Title */}
              <div className="d-flex align-items-center gap-3">
                {/* School Logo */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#111827',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Assign User Roles
                </h3>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <Container
          className="mt-4"
          style={{
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
            maxWidth: '1200px',
          }}
        >
          {/* Loading Spinner */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-3">Loading user data...</p>
            </div>
          )}

          {!loading && (
            <>
              {/* User Information Card */}
              <Card className="text-start"
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  marginBottom: '24px',
                }}
              >
                {/* Header with Icon */}
                <div
                  style={{
                    background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                    padding: '16px 24px',
                    borderBottom: '1px solid #D1D3D4',
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <BsPersonBadge size={18} style={{ color: '#ffffff' }} />
                    </div>
                    <h5
                      style={{
                        margin: 0,
                        fontWeight: '700',
                        fontSize: '16px',
                        color: '#ffffff',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      User Information
                    </h5>
                  </div>
                </div>
                <Card.Body style={{ padding: '24px' }}>
                  <Row className="g-3">
                    <Col md={6}>
                      <div>
                        <label className="form-label fw-semibold text-dark d-block mb-2">
                          <BsPersonBadge size={14} className="me-2" />
                          User Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={userData ? userData.name : 'Loading...'}
                          disabled
                          style={{
                            border: '1px solid #D1D3D4',
                            borderRadius: '8px',
                            padding: '10px 12px',
                            fontSize: '14px',
                            backgroundColor: '#F9FAFB',
                          }}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div>
                        <label className="form-label fw-semibold text-dark d-block mb-2">
                          <BsEnvelope size={14} className="me-2" />
                          Email Address
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={userData?.email || ''}
                          placeholder="Enter user email"
                          disabled
                          style={{
                            border: '1px solid #D1D3D4',
                            borderRadius: '8px',
                            padding: '10px 12px',
                            fontSize: '14px',
                            backgroundColor: '#F9FAFB',
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Current Roles Summary */}
              {selectedRoles.length > 0 && (
                <Card
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    marginBottom: '24px',
                  }}
                >
                  <Card.Body style={{ padding: '24px' }}>
                    <label className="form-label fw-semibold text-dark mb-3 d-flex align-items-center">
                      <BsCheckCircle size={16} className="me-2" />
                      Current Roles
                    </label>
                    <div className="d-flex flex-wrap gap-2">
                      {selectedRoles.map((role) => (
                        <span
                          key={role}
                          className={`badge bg-${getRoleColor(role)} px-3 py-2 rounded-pill`}
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                          }}
                        >
                          <BsCheckLg size={12} className="me-1" />
                          {allAvailableRoles.find(r => r.value === role)?.label || role}
                        </span>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Role Selection Card */}
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  marginBottom: '24px',
                }}
              >
                {/* Header with Icon */}
                <div
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    padding: '16px 24px',
                    borderBottom: '1px solid #D1D3D4',
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <BsListCheck size={18} style={{ color: '#ffffff' }} />
                    </div>
                    <h5
                      style={{
                        margin: 0,
                        fontWeight: '700',
                        fontSize: '16px',
                        color: '#ffffff',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Available Roles
                    </h5>
                  </div>
                </div>
                <Card.Body style={{ padding: '24px' }}>
                  <Row className="g-3">
                    {allAvailableRoles.map(role => {
                      const isDisabled = role.value === userType;
                      const isChecked = selectedRoles.includes(role.value);
                      return (
                        <Col key={role.value} md={6}>
                          <div
                            className={`card border-2 ${isChecked ? `border-${role.color}` : 'border-light'} ${isDisabled ? 'opacity-75' : ''}`}
                            style={{
                              borderRadius: '10px',
                              transition: 'all 0.2s ease',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() => !isDisabled && handleCheckboxChange(role.value)}
                          >
                            <div className="card-body p-3">
                              <div className="form-check d-flex align-items-center mb-0" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  className="form-check-input me-3"
                                  id={role.value}
                                  checked={isChecked}
                                  onChange={() => handleCheckboxChange(role.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  disabled={isDisabled}
                                  style={{ transform: "scale(1.2)", cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                                />
                                <label
                                  className={`form-check-label d-flex align-items-center w-100 ${isDisabled ? 'text-muted' : ''}`}
                                  htmlFor={role.value}
                                  style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                                >
                                  <span className="fs-4 me-3">{role.icon}</span>
                                  <div>
                                    <div className="fw-semibold">{role.label}</div>
                                  </div>
                                </label>
                              </div>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Card.Body>
              </Card>

              {/* Message Alert */}
              {message && (
                <div
                  className={`alert alert-${messageType === 'success' ? 'success' : messageType === 'error' ? 'danger' : 'info'} alert-dismissible fade show d-flex align-items-center`}
                  role="alert"
                  style={{
                    borderRadius: '10px',
                    marginBottom: '24px',
                  }}
                >
                  <i className={`bi ${messageType === 'success' ? 'bi-check-circle-fill' : messageType === 'error' ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'} me-2`}></i>
                  <div className="flex-grow-1">{message}</div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setMessage("")}
                  ></button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="d-flex gap-3 justify-content-end pt-3 border-top">
                <Button
                  variant="outline-secondary"
                  onClick={() => window.history.back()}
                  style={{
                    border: '1px solid #D1D3D4',
                    color: '#6b7280',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontWeight: '600',
                    fontSize: '15px',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '140px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#F3F4F6';
                    e.currentTarget.style.borderColor = '#9ca3af';
                    e.currentTarget.style.color = '#111827';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#D1D3D4';
                    e.currentTarget.style.color = '#6b7280';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <BsArrowLeft size={16} />
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !userData?.email}
                  style={{
                    backgroundColor: loading ? '#9ca3af' : '#223843',
                    border: 'none',
                    color: '#ffffff',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontWeight: '600',
                    fontSize: '15px',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '160px',
                    boxShadow: loading ? 'none' : '0 2px 4px rgba(37, 99, 235, 0.2)',
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = '#223843';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(37, 99, 235, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = '#223843';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(37, 99, 235, 0.2)';
                    }
                  }}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <BsCheckLg size={16} />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>

              {/* Help Text */}
              <div className="text-center mt-4">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Select one or multiple roles to assign to <span className="text-dark fw-semibold">{userData?.name}</span>. Changes take effect immediately.
                </small>
              </div>

              {/* Change Password Section */}
              <Card
                style={{
                  border: '2px solid #0EA5E9',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  marginTop: '32px',
                }}
              >
                {/* Header with Icon */}
                <div
                  style={{
                    background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
                    padding: '16px 24px',
                    borderBottom: '1px solid #D1D3D4',
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <BsKey size={18} style={{ color: '#ffffff' }} />
                    </div>
                    <h5
                      style={{
                        margin: 0,
                        fontWeight: '700',
                        fontSize: '16px',
                        color: '#ffffff',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Change Password
                    </h5>
                  </div>
                </div>
                <Card.Body style={{ padding: '24px' }}>
                  <div className="d-flex gap-3 align-items-start flex-wrap">
                    <div className="flex-grow-1" style={{ minWidth: "250px" }}>
                      <div className="input-group position-relative">
                        <span className="input-group-text bg-light" style={{ border: '1px solid #D1D3D4', borderRight: 'none' }}>
                          <BsKey size={14} className="text-muted" />
                        </span>
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          placeholder="Enter new password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          disabled={changingPassword}
                          style={{
                            border: '1px solid #D1D3D4',
                            borderRadius: '0',
                            padding: '10px 12px',
                            fontSize: '14px',
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={togglePasswordVisibility}
                          disabled={changingPassword}
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            borderLeft: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '10px 12px',
                          }}
                        >
                          {showPassword ? (
                            <FaEyeSlash fontSize={16} title="Hide" />
                          ) : (
                            <FaEye fontSize={16} title="Show" />
                          )}
                        </button>
                      </div>
                      <small className="text-muted d-block mt-2">
                        <i className="bi bi-info-circle me-1"></i>
                        Password must be at least 6 characters long
                      </small>
                    </div>
                    <div className="d-flex align-items-end">
                      <Button
                        onClick={handleChangePassword}
                        disabled={changingPassword || !newPassword.trim()}
                        style={{
                          backgroundColor: changingPassword || !newPassword.trim() ? '#9ca3af' : '#0EA5E9',
                          border: 'none',
                          color: '#ffffff',
                          borderRadius: '8px',
                          padding: '12px 24px',
                          fontWeight: '600',
                          fontSize: '15px',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.2s ease',
                          minHeight: "38px",
                          whiteSpace: "nowrap",
                          minWidth: '160px',
                        }}
                        onMouseEnter={(e) => {
                          if (!changingPassword && newPassword.trim()) {
                            e.currentTarget.style.backgroundColor = '#0284C7';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!changingPassword && newPassword.trim()) {
                            e.currentTarget.style.backgroundColor = '#0EA5E9';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {changingPassword ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              style={{ width: '14px', height: '14px', borderWidth: '2px' }}
                            ></span>
                            Changing...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-arrow-repeat me-2"></i>
                            Change Password
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </>
          )}
        </Container>
      </div>
    </>
  );
};

export default AssignUserRole;
